"use client";

import React, { useMemo } from "react";
import type { EChartsOption } from "echarts";
import { BaseChart, type BaseChartRef } from "./base-chart";
import {
  chartTooltip,
  chartLegend,
  chartGrid,
  chartCategoryAxis,
  chartValueAxis,
  chartTitle,
  chartAnimation,
  chartColors,
  chartColor,
} from "./chart-utils";
import { cn } from "@/src/utils/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BarChartSeries {
  /** Series name — shown in legend and tooltip */
  name: string;
  /** Data points for this series */
  data: number[];
  /** Optional override color (hex or CSS color) */
  color?: string;
}

export interface BarChartData {
  /** Category labels for X-axis */
  categories: string[];
  /** One or more data series */
  series: BarChartSeries[];
}

export interface BarChartProps {
  /** Data structure: categories + series */
  data: BarChartData;
  /** Chart title (optional) */
  title?: string;
  /** Chart subtitle (optional) */
  subtitle?: string;
  /** Chart height — CSS value or number (px). Default "100%" */
  height?: string | number;
  /** Extra class names for the wrapper */
  className?: string;
  /** If true, render horizontal bars (swap axes). Default false */
  horizontal?: boolean;
  /** If true, stack bars. Default false */
  stacked?: boolean;
  /** If true, show legend. Default: true if >1 series, else false */
  showLegend?: boolean;
  /** If true, show value labels on bars. Default false */
  showValues?: boolean;
  /** If true, show loading spinner overlay */
  loading?: boolean;
  /** If true, show empty state. Overrides data if both are true */
  empty?: boolean;
  /** Custom empty state message */
  emptyText?: string;
  /** Called when a bar is clicked: { name, seriesName, value } */
  onBarClick?: (params: {
    name: string;
    seriesName: string;
    value: number;
  }) => void;
  /** Optional value formatter (e.g., for currency or percentages) */
  formatValue?: (value: number) => string;
  /** Accessible label for screen readers */
  ariaLabel?: string;
}

// ---------------------------------------------------------------------------
// BarChart Component
// ---------------------------------------------------------------------------

export const BarChart = React.forwardRef<BaseChartRef, BarChartProps>(
  function BarChart(
    {
      data,
      title,
      subtitle,
      height = "100%",
      className,
      horizontal = false,
      stacked = false,
      showLegend,
      showValues = false,
      loading = false,
      empty = false,
      emptyText,
      onBarClick,
      formatValue,
      ariaLabel,
    },
    ref
  ) {
    // Build the ECharts option
    const option = useMemo<EChartsOption>(() => {
      const { categories, series } = data;

      // Determine if legend should show
      const shouldShowLegend = showLegend ?? series.length > 1;

      // Build series with colors and labels
      const chartSeries = series.map((s, idx) => ({
        type: "bar" as const,
        name: s.name,
        data: s.data,
        itemStyle: {
          color: s.color ?? chartColor(idx),
          borderRadius: horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0],
        },
        emphasis: {
          focus: "series" as const,
          blurScope: "coordinateSystem" as const,
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetY: 2,
            shadowColor: "rgba(0, 0, 0, 0.18)",
            borderWidth: 1,
            borderColor: "rgba(255, 255, 255, 0.4)",
          },
        },
        stack: stacked ? "value" : undefined,
        label: showValues
          ? {
              show: true,
              position: horizontal ? "right" as const : "top" as const,
              formatter: formatValue ? (p: any) => formatValue(p.value) : undefined,
              fontSize: 11,
              fontWeight: 500,
              distance: 4,
            }
          : undefined,
      }));

      // Build base option
      let baseOption: Record<string, any> = {
        ...(title && chartTitle(title, { subtitle })),
        ...chartTooltip({
          trigger: "axis",
          formatter: (params: any) => {
            if (!Array.isArray(params)) params = [params];
            let html = `<strong>${params[0]?.name}</strong><br/>`;
            params.forEach((p: any) => {
              const val = formatValue ? formatValue(p.value) : p.value;
              html += `<span style="display:inline-block;margin-right:8px;"><span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${p.color};margin-right:6px;"></span>${p.seriesName}: ${val}</span><br/>`;
            });
            return html;
          },
        }),
        ...(shouldShowLegend && chartLegend({ position: "bottom" })),
        ...chartGrid(),
        ...chartAnimation({ delay: (idx: number) => idx * 60 }),
      };

      // Add axes based on orientation
      if (horizontal) {
        // Horizontal: swap X and Y
        baseOption.xAxis = chartValueAxis();
        baseOption.yAxis = chartCategoryAxis(categories);
      } else {
        // Vertical (default)
        baseOption.xAxis = chartCategoryAxis(categories);
        baseOption.yAxis = chartValueAxis();
      }

      baseOption.series = chartSeries;

      return baseOption;
    }, [data, title, subtitle, horizontal, stacked, showLegend, showValues, formatValue]);

    // Handle bar click events
    const onEvents = useMemo(() => {
      if (!onBarClick) return undefined;
      return {
        click: (params: any) => {
          if (params.componentSubType === "bar") {
            onBarClick({
              name: params.name,
              seriesName: params.seriesName,
              value: params.value,
            });
          }
        },
      };
    }, [onBarClick]);

    // Generate data summary for accessibility
    const dataSummary = useMemo(() => {
      const { categories, series } = data;
      const lines = series.map(s => {
        const points = s.data.map((val, i) => `${categories[i]}: ${formatValue ? formatValue(val) : val}`).join(", ");
        return `${s.name}: ${points}`;
      });
      return lines.join(". ");
    }, [data, formatValue]);

    return (
      <BaseChart
        ref={ref}
        option={option}
        height={height}
        className={cn("bar-chart", className)}
        loading={loading}
        empty={empty}
        emptyText={emptyText}
        onEvents={onEvents}
        ariaLabel={ariaLabel || `Bar chart${title ? `: ${title}` : ""} showing ${data.series.map(s => s.name).join(", ")}`}
        dataSummary={dataSummary}
      />
    );
  }
);

BarChart.displayName = "BarChart";

export default BarChart;
